import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import { Responsive, WidthProvider } from 'react-grid-layout';
import MainContainer from '../../MainContainer';
import Tour from 'reactour';
import './TourContainer.css';
import { tourSteps, fakeDashboard } from './TourData';
import { useDispatch, useSelector } from 'react-redux';
import { seenTour } from '../../../redux/actions';
import Cookies from 'js-cookie';
import axios from 'axios';
import DashboardFilters from './DashboardFilters';
import Manage, { DEFAULT_LAYOUT, STORAGE_KEY } from './Manage';
import Widget from './Widget';
import styles from './Dashboard.module.css';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const authHeaders = () => ({
    Authorization: `Bearer ${Cookies.get('access_token')}`,
    'Content-Type': 'application/json',
});

const ResponsiveGridLayout = WidthProvider(Responsive);

const EMPTY_LAYOUTS = { lg: [], md: [], xs: [] };

const LOADING_LAYOUTS = {
    lg: [
        // stat cards row
        { i: 'l-0', x: 0,  y: 0, w: 3, h: 2 },
        { i: 'l-1', x: 3,  y: 0, w: 3, h: 2 },
        { i: 'l-2', x: 6,  y: 0, w: 3, h: 2 },
        { i: 'l-3', x: 9,  y: 0, w: 3, h: 2 },
        // second stat row
        { i: 'l-4', x: 0,  y: 2, w: 3, h: 2 },
        { i: 'l-5', x: 3,  y: 2, w: 3, h: 2 },
        { i: 'l-6', x: 6,  y: 2, w: 3, h: 2 },
        { i: 'l-7', x: 9,  y: 2, w: 3, h: 2 },
        // chart row
        { i: 'l-8', x: 0,  y: 4, w: 7, h: 6 },
        { i: 'l-9', x: 7,  y: 4, w: 5, h: 6 },
        // chart row 2
        { i: 'l-10', x: 0, y: 10, w: 4, h: 6 },
        { i: 'l-11', x: 4, y: 10, w: 8, h: 6 },
    ],
    md: [
        { i: 'l-0', x: 0,  y: 0, w: 3, h: 2 },
        { i: 'l-1', x: 3,  y: 0, w: 3, h: 2 },
        { i: 'l-2', x: 0,  y: 2, w: 3, h: 2 },
        { i: 'l-3', x: 3,  y: 2, w: 3, h: 2 },
        { i: 'l-4', x: 0,  y: 4, w: 3, h: 2 },
        { i: 'l-5', x: 3,  y: 4, w: 3, h: 2 },
        { i: 'l-6', x: 0,  y: 6, w: 2, h: 2 },
        { i: 'l-7', x: 2,  y: 6, w: 2, h: 2 },
        { i: 'l-8', x: 4,  y: 6, w: 2, h: 2 },
        { i: 'l-9',  x: 0, y: 8, w: 4, h: 6 },
        { i: 'l-10', x: 4, y: 8, w: 2, h: 6 },
        { i: 'l-11', x: 0, y: 14, w: 4, h: 6 },
    ],
    xs: [
        { i: 'l-0', x: 0, y: 0,  w: 2, h: 2 },
        { i: 'l-1', x: 0, y: 2,  w: 2, h: 2 },
        { i: 'l-2', x: 0, y: 4,  w: 2, h: 2 },
        { i: 'l-3', x: 0, y: 6,  w: 2, h: 2 },
        { i: 'l-4', x: 0, y: 8,  w: 2, h: 2 },
        { i: 'l-5', x: 0, y: 10, w: 2, h: 2 },
        { i: 'l-6', x: 0, y: 12, w: 2, h: 2 },
        { i: 'l-7', x: 0, y: 14, w: 2, h: 2 },
        { i: 'l-8', x: 0, y: 16, w: 2, h: 6 },
        { i: 'l-9', x: 0, y: 22, w: 2, h: 6 },
        { i: 'l-10', x: 0, y: 28, w: 2, h: 6 },
        { i: 'l-11', x: 0, y: 34, w: 2, h: 6 },
    ],
};

const Dashboard = () => {
    const dispatch = useDispatch();
    const user          = useSelector(state => state?.userReducer.user);
    const dashboardData = useSelector(state => state?.dashboardReducer.dashboard);
    const dashboardLoading = useSelector(state => state?.dashboardReducer.loading);

    const [showTour,      setShowTour]      = useState(false);
    const [dashboard,     setDashboard]     = useState(null);   // resolved data (real or fake)
    const [useSampleData, setUseSampleData] = useState(false);
    const [filtersActive, setFiltersActive] = useState(false);
    const [hasAnyTickets, setHasAnyTickets] = useState(null);  // null = unknown yet

    // grid state
    const [anyFilterOpen, setAnyFilterOpen] = useState(false);
    const stickyHeaderRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (stickyHeaderRef.current && !stickyHeaderRef.current.contains(e.target)) {
                setAnyFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const [editable,      setEditable]      = useState(false);
    const [manageEdit,    setManageEdit]    = useState(false);

    // When manage mode exits (Discard or Save), clear the sticky. Filters are hidden
    // while in manage mode so anyFilterOpen may still be true from before.
    useEffect(() => {
        if (!manageEdit) setAnyFilterOpen(false);
    }, [manageEdit]);

    const [widgets,       setWidgets]       = useState([]);
    const [layouts,       setLayouts]       = useState(EMPTY_LAYOUTS);
    const [nextId,        setNextId]        = useState(0);
    const [layoutReady,   setLayoutReady]   = useState(false);

    // loading is true until BOTH the layout fetch AND dashboard data are resolved
    const loading = !layoutReady || dashboard === null;

    // after the first successful load, show skeletons on filter refetch too
    const initialLoadDone = layoutReady && dashboard !== null;
    const showSkeleton = loading || (initialLoadDone && dashboardLoading);

    // snapshot taken when "Manage Dashboard" is opened — used by Discard Changes
    const [snapshot,      setSnapshot]      = useState(null);

    // ── resolve dashboard data (same logic as before) ─────────────────────────
    useEffect(() => {
        if (user !== undefined) {
            const hasSeenTour  = checkSeenTour();
            const hasNoDataForFilters = !filtersActive && checkTicketsSum(dashboardData);
            const anyTickets = dashboardData?.hasAnyTickets ?? hasAnyTickets;
            if (anyTickets !== null) setHasAnyTickets(anyTickets);
            // Only use fake/sample data for genuinely new users (no tickets at all)
            const showSample = hasNoDataForFilters && !anyTickets;
            setDashboard(showSample ? fakeDashboard : dashboardData);
            setUseSampleData(showSample);
            setShowTour(!hasSeenTour && !anyTickets);
        }
    }, [user, dashboardData, filtersActive]);

    const checkSeenTour = () => {
        const seen = Cookies.get('seen_tour');
        return +seen === 1 || user.seen_tour === 1;
    };

    const checkTicketsSum = (data) => {
        if (data === null || Object.keys(data || {}).length === 0) return false;
        const ticketsSum =
            Object.values(data?.dailyTickets  || []).reduce((s, a) => s + a, 0) +
            Object.values(data?.ticketsCount  || []).reduce((s, a) => s + a, 0);
        return ticketsSum === 0;
    };

    const closeTour = () => {
        Cookies.set('seen_tour', 1);
        dispatch(seenTour());
        setShowTour(false);
    };

    // ── load layout: API first, localStorage as offline fallback ─────────────
    useEffect(() => {
        const applyLayout = (source) => {
            setWidgets(source.widgets);
            setLayouts(source.layouts);
            const ids = source.widgets.map(w => parseInt(w.layout_id.split('-')[1]));
            setNextId(ids.length > 0 ? Math.max(...ids) + 1 : 0);
            setLayoutReady(true);
        };

        axios.get('/api/dashboard-layout', { headers: authHeaders() })
            .then(({ data }) => {
                // treat any valid response (even empty widgets) as authoritative
                if (data && Array.isArray(data.widgets) && data.layouts) {
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
                    applyLayout(data);
                } else {
                    const saved = (() => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY)); } catch { return null; } })();
                    applyLayout(saved || DEFAULT_LAYOUT);
                }
            })
            .catch(() => {
                const saved = (() => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY)); } catch { return null; } })();
                applyLayout(saved || DEFAULT_LAYOUT);
            });
    }, []);

    // ── drag from widget panel ─────────────────────────────────────────────────
    const [droppingWidget, setDroppingWidget] = useState(null);

    const handleDragStart = (e, widget) => {
        e.dataTransfer.setData('widget', JSON.stringify(widget));
        setDroppingWidget(widget);
    };

    // ── tap-to-add (mobile) — appends widget to the bottom of the grid ────────
    const handleAddWidget = useCallback((widget) => {
        const layout_id = `layout-${nextId}`;
        const sz = widget.size || {};
        const newItem = { ...widget, widget_id: widget.code, layout_id };

        // shift every existing item down to make room at the top
        const shiftDown = (items, h) => items.map(it => ({ ...it, y: it.y + h }));

        const gridItem = {
            i:    layout_id,
            x:    0,
            y:    0,
            w:    sz.w    ?? 6,
            h:    sz.h    ?? 4,
            minW: sz.minW ?? 2,
            minH: sz.minH ?? 2,
            maxW: sz.maxW ?? 12,
            maxH: sz.maxH ?? 10,
        };

        setWidgets(prev => [newItem, ...prev]);
        setLayouts(prev => ({
            lg: [gridItem, ...shiftDown(prev.lg, gridItem.h)],
            md: [{ ...gridItem, w: Math.min(gridItem.w, 6), maxW: 6 }, ...shiftDown(prev.md, gridItem.h)],
            xs: [{ ...gridItem, w: 2, maxW: 2 }, ...shiftDown(prev.xs, gridItem.h)],
        }));
        setNextId(id => id + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [nextId, layouts]);

    const handleDrop = (layout, layoutItem, e) => {
        const widget    = JSON.parse(e.dataTransfer.getData('widget'));
        const layout_id = `layout-${nextId}`;
        const sz        = widget.size || {};
        const newItem   = { ...widget, widget_id: widget.code, layout_id };

        const gridItem = {
            i:    layout_id,
            x:    layoutItem.x,
            y:    layoutItem.y,
            w:    sz.w    ?? 6,
            h:    sz.h    ?? 4,
            minW: sz.minW ?? 2,
            minH: sz.minH ?? 2,
            maxW: sz.maxW ?? 12,
            maxH: sz.maxH ?? 10,
        };

        // layout[] from RGL still has '__dropping-elem__' — replace it with our real item
        const mergedLg = layout.map(it =>
            it.i === '__dropping-elem__' ? gridItem : it
        );

        setWidgets(prev => [...prev, newItem]);
        setLayouts(prev => ({
            lg: mergedLg,
            md: [...prev.md, { ...gridItem, w: Math.min(gridItem.w, 6), maxW: 6 }],
            xs: [...prev.xs, { ...gridItem, w: 2, maxW: 2 }],
        }));
        setNextId(id => id + 1);
        setDroppingWidget(null);
    };

    const handleRemoveWidget = useCallback((layout_id) => {
        setWidgets(prev => prev.filter(w => w.layout_id !== layout_id));
        setLayouts(prev =>
            Object.keys(prev).reduce((acc, bp) => {
                acc[bp] = prev[bp].filter(item => item.i !== layout_id);
                return acc;
            }, { ...prev })
        );
    }, []);

    const handleLayoutChange = (_current, allLayouts) => {
        setLayouts(allLayouts);
    };

    // save to localStorage only on explicit save (via saveLayoutToServer)

    const saveLayoutToServer = (currentWidgets, currentLayouts) => {
        return axios.post(
            '/api/dashboard-layout',
            { widgets: currentWidgets, layouts: currentLayouts },
            { headers: authHeaders() }
        ).then(r => {
            // only persist to localStorage after a confirmed DB save
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ widgets: currentWidgets, layouts: currentLayouts }));
            return r.data;
        });
    };

    // ── grid items ────────────────────────────────────────────────────────────
    const gridItems = useMemo(() =>
        widgets.map(widget => (
            <div key={widget.layout_id}>
                <Widget
                    widget={widget}
                    onRemove={handleRemoveWidget}
                    editable={editable}
                    dashboardData={dashboard}
                    useSampleData={useSampleData}
                />
            </div>
        )),
    [widgets, editable, dashboard, useSampleData, handleRemoveWidget]);

    const loadingItems = ['l-0','l-1','l-2','l-3','l-4','l-5','l-6','l-7','l-8','l-9','l-10','l-11'].map(k => (
        <div key={k}><div className={styles.skeletonCard} /></div>
    ));

    // ── render ────────────────────────────────────────────────────────────────
    return (
        <MainContainer>
            <Container fluid="md" className={(useSampleData ? 'sample-data ' : '') + 'pt-1'} style={{ position: 'relative' }}>
                {/* Title + Filters + manage controls */}
                <div ref={stickyHeaderRef} className={(manageEdit || anyFilterOpen) ? styles.stickyManageBar : ''}>
                <Row className="pt-4 pb-3" style={{ alignItems: 'flex-start' }}>
                    <Col xs={12} sm="auto" className='text-align-left'>
                        <h5 style={{ margin: 0, whiteSpace: 'nowrap', paddingTop: '5px' }}>Dashboard</h5>
                    </Col>
                    <Col xs={12} sm style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap', minWidth: 0 }}>
                        <div style={{ display: manageEdit ? 'none' : 'contents' }}><DashboardFilters isLoading={dashboardLoading} onFiltersActive={setFiltersActive} onAnyOpen={setAnyFilterOpen} /></div>
                        <Manage
                            onDragStart={handleDragStart}
                            onAddWidget={handleAddWidget}
                            onEditable={setEditable}
                            onLayoutChange={setLayouts}
                            onWidgetsChange={setWidgets}
                            onSave={saveLayoutToServer}
                            currentWidgets={widgets}
                            currentLayouts={layouts}
                            snapshot={snapshot}
                            edit={manageEdit}
                            onEditChange={setManageEdit}
                            onEnterEdit={() => { setSnapshot({ widgets, layouts }); setManageEdit(true); setEditable(true); }}
                            isLoading={showSkeleton}
                        />
                    </Col>
                </Row>
                </div>



                {/* Sample data banner — only shown to genuinely new users with no tickets at all */}
                {useSampleData && !showSkeleton && (
                    <div className={styles.sampleDataBanner}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                        </svg>
                        <span>You're viewing <strong>sample data</strong> — create your first ticket to see real stats.</span>
                        <Link to="/tickets?sort_by=created_at&sort_direction=desc&page=1&per_page=10" className={styles.sampleDataCta}>Go to Tickets →</Link>
                    </div>
                )}

                {/* Grid — negative mx to cancel RGL's outer gutter so cards align with the title */}
                <div style={{ margin: '0 -12px' }}>
                <ResponsiveGridLayout
                    className={styles.layout}
                    layouts={showSkeleton ? LOADING_LAYOUTS : layouts}
                    breakpoints={{ lg: 1140, md: 728, xs: 516 }}
                    cols={{ lg: 12, md: 6, xs: 2 }}
                    rowHeight={40}
                    margin={[12, 12]}
                    onLayoutChange={showSkeleton ? undefined : handleLayoutChange}
                    onDrop={handleDrop}
                    isDroppable={editable}
                    isDraggable={editable}
                    isResizable={editable}
                    draggableHandle=".widget-drag-handle"
                    draggableCancel=".widget-no-drag"
                    droppingItem={droppingWidget ? {
                        i: '__dropping-elem__',
                        w: droppingWidget.size?.w    ?? 6,
                        h: droppingWidget.size?.h    ?? 4,
                    } : undefined}
                >
                    {showSkeleton ? loadingItems : gridItems}
                </ResponsiveGridLayout>
                </div>

                {widgets.length === 0 && !showSkeleton && (
                    <div className={styles.emptyState}>
                        <span>No widgets on the dashboard.</span>
                        <span style={{ fontSize: '.85rem' }}>Click <strong>Manage Dashboard</strong> then drag widgets onto the grid.</span>
                    </div>
                )}
            </Container>

            {user && (
                <Tour
                    steps={tourSteps}
                    isOpen={showTour}
                    onRequestClose={closeTour}
                    disableInteraction={true}
                    lastStepNextButton="Done"
                    showButtons={true}
                    className="tour-dashboard"
                />
            )}
        </MainContainer>
    );
};

export default Dashboard;
